import Image from 'next/image';
import { useState, useRef, useEffect } from 'react';
import { MotionValue } from 'motion/react';
import { useInView } from 'react-intersection-observer';
import { ArrowAngle } from 'components/library/svgs--foldertentative/arrow-angle';
import {
  Play,
  Pause,
} from 'components/library/svgs--foldertentative/play-pause';
import { CSSProperties } from 'react';
import { Frame, ScrollerWrapper, ScrollerButton } from './scroller.styled';
import MuxPlayer from 'components/reusable/MuxPlayer/mux-player';
import { buttonEventHandler } from 'lib/utils/button-event-handler';
export type scrollerTypes = 'simple' | 'double';
export type ratios = '14:9' | '1:1' | '9:14';
export type shapes = 'round' | 'vertical' | 'horizontal' | 'horizontal-pill';
export interface ItemProps {
  cover?: {
    src: string;
    width: number;
    height: number;
    alt: string;
  };
  video?: {
    playbackId: string;
    devPlaybackId: string;
    format?: string;
    title?: string;
  };
  videoLoop?: boolean;
  url?: string;
  ratio?: ratios | undefined;
  shape?: shapes | undefined;
}

interface IInlineScrollerProps {
  items: ItemProps[];
  direction: string;
  progress: MotionValue<number>;
  left: string;
  type: scrollerTypes;
}

const Scroller = ({ items, type }: IInlineScrollerProps) => {
  // track playing states across multiple videos
  const [playingStates, setPlayingStates] = useState<Record<number, boolean>>(
    {},
  );
  const videoRefs = useRef<Array<HTMLVideoElement | null>>([]);
  // Observes the whole row: video items start outside the horizontal scroll area.
  const { ref: wrapperRef, inView: isNearView } = useInView({
    rootMargin: '100%',
    triggerOnce: true,
  });
  const [aspectRatio, setAspectRatio] = useState<string>('');
  // inject aspect ratio after mount, very important
  useEffect(() => {
    setAspectRatio('9 / 16');
  }, []);

  const ratios = items.map((img: ItemProps) => {
    switch (img?.shape) {
      case 'horizontal':
        return '14/9';
      case 'horizontal-pill':
        return '14/9';
      case 'round':
        return '1/1';
      default:
        return '9/14';
    }
  });

  const doubleWidths = items.map((img: ItemProps) => {
    switch (img?.ratio) {
      case '14:9':
        return '270';
      case '1:1':
        return '260';
      case '9:14':
        return '238';
      default:
        return '238';
    }
  });

  const simpleWidths = items.map((img: ItemProps) => {
    switch (img?.shape) {
      case 'round':
        return '430';
      case 'vertical':
        return '276';
      case 'horizontal':
        return '322';
      default:
        return '322';
    }
  });

  const handleClick = (item: ItemProps) => {
    if (item.url) {
      buttonEventHandler(`Click link to ${item.url}`, 'UGC Scroller');
    }
  };

  const handleVideoClick = (index: number) => {
    const current = videoRefs.current[index];
    if (current) {
      if (current.paused) {
        current.play();
        setPlayingStates((prev) => ({ ...prev, [index]: true }));
        buttonEventHandler(`Play video ${index}`, 'UGC Scroller');
      } else {
        current.pause();
        setPlayingStates((prev) => ({ ...prev, [index]: false }));
        buttonEventHandler(`Pause video ${index}`, 'UGC Scroller');
      }
    }
  };

  return (
    <ScrollerWrapper ref={wrapperRef} type={type}>
      {items.map((item: ItemProps, index: number) => {
        const ratio = item?.ratio
          ? item?.ratio.replace(':', '/')
          : ratios[index];

        const width =
          type == 'simple' ? simpleWidths[index] : doubleWidths[index];

        const isPlaying = playingStates[index] || false;

        return (
          <Frame
            key={index}
            as={item.url ? 'a' : 'div'}
            href={item.url}
            target='_blank'
            style={
              {
                '--ratio': ratio,
                '--width': `${width}px`,
              } as CSSProperties
            }
            shape={item?.shape}
            type={type}
            onClick={() => handleClick(item)}
          >
            {item.url && (
              <ScrollerButton>
                <ArrowAngle color='#fcfcf7' />
              </ScrollerButton>
            )}
            {item.video && !item.videoLoop && (
              <ScrollerButton onClick={() => handleVideoClick(index)}>
                {isPlaying ? (
                  <Pause color='#fcfcf7' />
                ) : (
                  <Play color='#fcfcf7' />
                )}
              </ScrollerButton>
            )}
            {item?.video && isNearView && (
              <div onClick={() => handleVideoClick(index)}>
                <MuxPlayer
                  playbackId={
                    process.env.NEXT_PUBLIC_STAGING === 'true'
                      ? item.video.devPlaybackId
                      : item.video.playbackId
                  }
                  wrapperPosition='relative'
                  videoRefs={videoRefs}
                  onClick={() => handleVideoClick(index)}
                  index={index}
                  isManuallyControlled={true}
                  lazy={false}
                  thumbnailTime={0}
                  // 3x the frame width covers phone screens.
                  posterWidth={Number(width) * 3}
                  preload='metadata'
                  style={{
                    /*@ts-ignore*/
                    '--controls': 'none',
                    ...(aspectRatio ? { aspectRatio } : {}),
                  }}
                  // we are sorry about this line ^^
                />
              </div>
            )}
            {item.cover && (
              <Image
                key={index}
                src={item.cover.src}
                width={item.cover.width}
                height={item.cover.height}
                alt={item.cover.alt}
              />
            )}
          </Frame>
        );
      })}
    </ScrollerWrapper>
  );
};

export default Scroller;
